import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { usePermission } from "../../providers/PermissionsProvider";
import { PageLoader } from "../page-loader";
import { getAdminUserDetail } from "../../services/admin.service";
import { prefetchAdminUserDetail } from "../../services/user-detail-prefetch";
import { prefetchUserDetailPage } from "../../routing/lazy-pages";

/**
 * PermissionRoute - Route guard that checks permissions
 * 
 * DENY-BY-DEFAULT: Redirects to /dashboard if user lacks permission
 * 
 * Usage:
 *   <PermissionRoute permission="admin:security" component={AdminPage} />
 *   <PermissionRoute permission={["admin:security", "admin:provisioning"]} component={Page} />
 */
export const PermissionRoute = ({ permission, component: Component }) => {
  const { can, canAny, loading } = usePermission();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const location = useLocation();

  // Overlap user-detail JS chunk + API fetch with the permissions gate.
  useEffect(() => {
    const match = location.pathname.match(/^\/admin\/users\/(\d+)\/?$/);
    if (!match || !isAuthenticated) return undefined;
    prefetchUserDetailPage();
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        if (!cancelled) {
          prefetchAdminUserDetail(token, match[1], getAdminUserDetail);
        }
      } catch {
        // Prefetch is best-effort; the page will fetch on mount if needed.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, isAuthenticated, getAccessTokenSilently]);
  
  if (loading) {
    return <PageLoader />;
  }

  const hasPermission = Array.isArray(permission)
    ? canAny(permission)
    : can(permission);

  if (!hasPermission) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Component />;
};
